import React, { useEffect, useRef, useState } from "react";
import Spinner from "../Spinner";
import DropdownPortal from "../DropdownPortal";
import { MdClose } from "react-icons/md";

const defaultGetOptionLabel = (option) => option?.name ?? "";

const Autocomplete = ({
  options = [],
  value = null,
  onChange,
  onInputChange,
  getOptionLabel = defaultGetOptionLabel,
  renderOption,
  loading = false,
  label,
  placeholder,
  noOptionsText = "",
  disabled = false,
  multiple = false,
  className = "",
  style,
}) => {
  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : null;
  const safeGetOptionLabel = (val) => (val == null ? "" : getOptionLabel(val));
  const [inputValue, setInputValue] = useState(multiple ? "" : safeGetOptionLabel(value));
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!multiple) setInputValue(safeGetOptionLabel(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        !listRef.current?.contains(event.target)
      ) {
        setOpen(false);
        if (!multiple) setInputValue(safeGetOptionLabel(value));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value]);

  const filteredOptions = options
    .filter((option) =>
      getOptionLabel(option).toLowerCase().includes(inputValue.trim().toLowerCase())
    )
    .filter((option) =>
      multiple ? !selectedValues.some((v) => v?.id === option?.id) : true
    );

  const handleSelect = (option) => {
    if (multiple) {
      onChange?.([...selectedValues, option]);
      setInputValue("");
    } else {
      onChange?.(option);
      setInputValue(getOptionLabel(option));
      setOpen(false);
    }
  };

  const handleRemove = (option) => {
    onChange?.(selectedValues.filter((v) => v?.id !== option?.id));
  };

  return (
    <div ref={containerRef} style={style} className={`relative w-full ${className}`}>
      {label && (
        <span className="mb-1 block text-xs text-farmazon-text-muted">{label}</span>
      )}
      {multiple && selectedValues.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {selectedValues.map((option, index) => (
            <span
              key={option?.id ?? index}
              className="inline-flex items-center gap-1 rounded-full bg-farmazon-hover px-2.5 py-1 text-xs text-farmazon-text"
            >
              {getOptionLabel(option)}
              <button
                type="button"
                onClick={() => handleRemove(option)}
                className="flex items-center justify-center rounded-full hover:bg-black/10"
              >
                <MdClose size={14} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div ref={inputWrapperRef} className="relative">
        <input
          type="text"
          disabled={disabled}
          value={inputValue}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setInputValue(event.target.value);
            onInputChange?.(event.target.value);
            setOpen(true);
          }}
          className="h-10 w-full rounded-lg border border-solid border-[var(--farmazon-input-border)] bg-[var(--farmazon-input)] px-3 pr-8 text-sm text-farmazon-text outline-none transition-colors placeholder:text-farmazon-text-faint hover:border-slate-400 focus:border-green-600 focus:ring-2 focus:ring-green-600/10 disabled:cursor-not-allowed disabled:opacity-60"
        />
        {loading && (
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
            <Spinner size="xs" color="inherit" />
          </span>
        )}
      </div>
      <DropdownPortal
        ref={listRef}
        anchorRef={inputWrapperRef}
        open={open && !disabled && (filteredOptions.length > 0 || !!noOptionsText)}
      >
        {filteredOptions.length > 0
          ? filteredOptions.map((option, index) => (
              <div
                key={option?.id ?? index}
                onClick={() => handleSelect(option)}
                className="cursor-pointer px-3 py-2 text-sm text-farmazon-text hover:bg-farmazon-hover"
              >
                {renderOption ? renderOption(option) : getOptionLabel(option)}
              </div>
            ))
          : (
              <div className="px-3 py-2 text-sm text-farmazon-text-faint">
                {noOptionsText}
              </div>
            )}
      </DropdownPortal>
    </div>
  );
};

export default Autocomplete;
